import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  X,
  Shield,
  Droplets,
  Star,
  Clock,
  DollarSign,
  Navigation,
  Heart,
  MessageSquare,
  Sparkles,
  MapPin,
  Flag,
  Lightbulb,
  User,
  Accessibility,
  Baby,
  Lock,
  SprayCanIcon,
  Flame,
  HandHeart,
} from "lucide-react";

import AdoptModal from "@/components/AdoptModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTRIBUTE_ICONS = {
  well_lit: Lightbulb,
  has_attendant: User,
  wheelchair_accessible: Accessibility,
  baby_changing: Baby,
  separate_facilities: User,
  working_locks: Lock,
  soap_available: SprayCanIcon,
  running_water: Droplets,
};

export default function RestroomDetail({ restroom, onClose, onReview, onFavorite, onDirections, getRatingColor, refreshKey = 0 }) {
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [sponsor, setSponsor] = useState(null);
  const [incin, setIncin] = useState({ needed_count: 0, has_incinerator: false, eligible: false, user_voted: false });
  const [showAdopt, setShowAdopt] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    setLoadingReviews(true);
    fetchReviews();
    fetchSponsor();
    fetchIncin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restroom.restroom_id, refreshKey]);

  const fetchSponsor = async () => {
    try {
      const r = await axios.get(`${API}/restrooms/${restroom.restroom_id}/sponsor`);
      setSponsor(r.data?.sponsor_name ? r.data : null);
    } catch (_) {}
  };

  const fetchIncin = async () => {
    try {
      const r = await axios.get(`${API}/restrooms/${restroom.restroom_id}/incinerator-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setIncin(r.data);
    } catch (_) {}
  };

  const voteIncinerator = async () => {
    if (!token) { toast.error("Please log in to vote"); return; }
    try {
      await axios.post(
        `${API}/restrooms/${restroom.restroom_id}/incinerator-vote`,
        { note: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Vote recorded — admin will plan an incinerator install.");
      fetchIncin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record vote");
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${restroom.restroom_id}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const openDirections = () => {
    // Prefer the in-app turn-by-turn modal if wired up; fall back to Google Maps.
    if (typeof onDirections === "function") {
      onDirections();
      return;
    }
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${restroom.latitude},${restroom.longitude}`,
      "_blank"
    );
  };

  return (
    <div className="space-y-4" data-testid="restroom-detail">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900">{restroom.name}</h2>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1" data-testid="restroom-location">
            <MapPin size={14} />
            {(() => {
              const cityState = [restroom.city, restroom.state].filter(Boolean).join(", ");
              const addr = restroom.address || "";
              // Prefer the derived "City, State" — always accurate to the pin.
              // Only append the raw address if it's meaningfully more specific
              // (e.g. contains a street, plot number, or landmark keyword).
              const looksSpecific = /\d|road|street|st\b|nagar|colony|marg|lane|opposite|near|block|floor|market|junction/i.test(addr);
              return looksSpecific && cityState && !addr.toLowerCase().includes(restroom.city?.toLowerCase() || "___")
                ? `${addr} · ${cityState}`
                : (cityState || addr);
            })()}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"
        >
          <X size={18} />
        </button>
      </div>

      {/* Ratings */}
      <div className="flex gap-4 p-4 bg-slate-50 rounded-xl">
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <Shield size={20} className={getRatingColor(restroom.safety_rating)} />
            <span className="text-2xl font-bold">{restroom.safety_rating?.toFixed(1) ?? "3.0"}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Safety</p>
        </div>
        <div className="w-px bg-slate-200" />
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <Droplets size={20} className={getRatingColor(restroom.cleanliness_rating)} />
            <span className="text-2xl font-bold">{restroom.cleanliness_rating?.toFixed(1) ?? "3.0"}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Cleanliness</p>
        </div>
        <div className="w-px bg-slate-200" />
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <Star size={20} className="text-amber-500" />
            <span className="text-2xl font-bold">{restroom.total_reviews}</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Reviews</p>
        </div>
      </div>

      {/* Badges */}
      <div className="flex gap-2 flex-wrap">
        {restroom.type === "paid" && (
          <Badge variant="secondary">
            <DollarSign size={14} className="mr-1" />
            ₹{restroom.price || "Paid"}
          </Badge>
        )}
        {restroom.type === "free" && (
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            Free
          </Badge>
        )}
        {restroom.is_24_hours ? (
          <Badge variant="secondary">
            <Clock size={14} className="mr-1" />
            Open 24h
          </Badge>
        ) : restroom.opening_time && (
          <Badge variant="secondary">
            <Clock size={14} className="mr-1" />
            {restroom.opening_time} - {restroom.closing_time}
          </Badge>
        )}
        {restroom.verified && (
          <Badge className="bg-sky-100 text-sky-700">
            <Sparkles size={14} className="mr-1" />
            Verified
          </Badge>
        )}
      </div>

      {/* Attributes */}
      {restroom.attributes && restroom.attributes.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm text-slate-700">Facilities</h3>
          <div className="flex gap-2 flex-wrap">
            {restroom.attributes.map((attr) => {
              const Icon = ATTRIBUTE_ICONS[attr] || Star;
              return (
                <Badge key={attr} variant="outline" className="text-xs">
                  <Icon size={12} className="mr-1" />
                  {attr.replace(/_/g, " ")}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={openDirections}
          className="flex-1 rounded-full bg-sky-600 hover:bg-sky-700"
          data-testid="get-directions-btn"
        >
          <Navigation size={18} className="mr-2" />
          Directions
        </Button>
        <Button
          variant="outline"
          onClick={onFavorite}
          className="rounded-full"
          data-testid="favorite-btn"
        >
          <Heart size={18} />
        </Button>
        <Button
          variant="outline"
          onClick={onReview}
          className="rounded-full"
          data-testid="leave-review-btn"
        >
          <MessageSquare size={18} />
        </Button>
      </div>

      {/* Sponsor badge */}
      {sponsor && (
        <div
          className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3"
          data-testid="sponsor-badge"
        >
          <HandHeart size={20} className="text-emerald-700" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-emerald-700 font-semibold">Adopted by</p>
            <p className="text-sm font-bold text-emerald-900 truncate" data-testid="sponsor-name">
              {sponsor.sponsor_company || sponsor.sponsor_name}
            </p>
            {sponsor.includes_incinerator && (
              <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
                <Flame size={11} /> Incinerator install funded
              </p>
            )}
          </div>
          {sponsor.expires_at && (
            <span className="text-xs text-emerald-700">
              until {new Date(sponsor.expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* Adopt + Incinerator actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          onClick={() => setShowAdopt(true)}
          className="bg-rose-600 hover:bg-rose-700"
          data-testid="adopt-btn"
        >
          <HandHeart size={16} className="mr-2" />
          {sponsor ? "Adopt again" : "Adopt this restroom"}
        </Button>
        {incin.eligible && (
          incin.has_incinerator ? (
            <div className="inline-flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="incinerator-installed">
              <Flame size={14} /> Incinerator installed
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={voteIncinerator}
              disabled={incin.user_voted}
              data-testid="incinerator-vote-btn"
            >
              <Flame size={16} className="mr-2 text-orange-500" />
              {incin.user_voted
                ? `You voted (${incin.needed_count} total)`
                : `Request incinerator (${incin.needed_count})`}
            </Button>
          )
        )}
      </div>

      {/* Reviews Section */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Recent Reviews</h3>
          <span className="text-sm text-slate-500">{reviews.length} reviews</span>
        </div>

        {loadingReviews ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-6 bg-slate-50 rounded-xl">
            <MessageSquare size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">No reviews yet. Be the first!</p>
          </div>
        ) : (
          <ScrollArea className="max-h-48">
            <div className="space-y-3 pr-2">
              {reviews.map((review) => (
                <motion.div
                  key={review.review_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-3 bg-slate-50 rounded-xl"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      {review.is_anonymous ? (
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                          <User size={14} className="text-slate-500" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                          <span className="text-sky-600 font-medium text-sm">
                            {review.user_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm text-slate-900">
                          {review.is_anonymous ? "Anonymous" : review.user_name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Shield size={12} className={getRatingColor(review.safety_rating)} />
                        <span className="text-xs font-medium">{review.safety_rating}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Droplets size={12} className={getRatingColor(review.cleanliness_rating)} />
                        <span className="text-xs font-medium">{review.cleanliness_rating}</span>
                      </div>
                    </div>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-slate-600 mt-2">{review.comment}</p>
                  )}
                  {review.photos && review.photos.length > 0 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar">
                      {review.photos.map((p, i) => (
                        <a
                          key={i}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-shrink-0"
                          data-testid={`review-photo-thumb-${i}`}
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                            alt=""
                            className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                            loading="lazy"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {review.attributes && review.attributes.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {review.attributes.slice(0, 3).map((attr) => (
                        <span key={attr} className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                          {attr.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      <AdoptModal
        open={showAdopt}
        onClose={() => { setShowAdopt(false); fetchSponsor(); }}
        restroom={restroom}
      />
    </div>
  );
}
